import { FlexColumn, FlexRow, MaterialIcon, StyledButton, useLoading, useModal } from '@gorila-shared-ui/components';
import { HeadingSmall, ParagraphMedium } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { deleteUser, postUser, updateUser } from '../../../services/usersService';
import { apiParamsState } from '../../../storage/app';
import {
  changeUserPasswordState,
  editingUserState,
  selectedUserIdState,
  updateUsersState,
  userState,
  userValidPasswordState,
} from '../../../storage/users';
import ConfirmModal from '../../shared/ConfirmModal';
import { UserForm } from './UserForm';

export function UserDetail() {
  const { theme, css } = useStyles();
  const { openModal, closeModal, showModal } = useModal();
  const { loading, startLoading, stopLoading } = useLoading();
  const [editingUser, setEditingUser] = useRecoilState(editingUserState);
  const resetEditingUser = useResetRecoilState(editingUserState);
  const apiParams = useRecoilValue(apiParamsState);
  const [selectedUserId, setSelectedUserId] = useRecoilState(selectedUserIdState);
  const user = useRecoilValue(userState(selectedUserId || ''));
  const resetSelectedUserId = useResetRecoilState(selectedUserIdState);
  const setUpdateUsers = useSetRecoilState(updateUsersState);
  const [changePassword, setChangePassword] = useRecoilState(changeUserPasswordState);
  const validPassword = useRecoilValue(userValidPasswordState);
  const resetChangePassword = useResetRecoilState(changeUserPasswordState);
  const { showPositiveFeedback, showDeletionFeedback, showFailFeedback } = useFeedback();
  const isNewUser = editingUser?._id === '';
  const [resetEmailField, setResetEmailField] = useState(false);

  useEffect(() => {
    resetEditingUser();
    resetChangePassword();
    setResetEmailField(!resetEmailField);
    return () => {
      resetEditingUser();
      resetChangePassword();
    };
  }, [selectedUserId]);

  if (editingUser === undefined) return null;

  const canSubmit = changePassword
    ? validPassword
    : (!!editingUser.email ? true : false) && !!editingUser.username && (isNewUser ? validPassword : true);
  const hasUpdates = changePassword ? true : !areEqual(user || {}, editingUser);

  const onDeleteUser = async () => {
    if (!apiParams || selectedUserId === undefined) return;
    const { error } = await deleteUser(apiParams.clientId, apiParams.subClientId, selectedUserId);
    if (!error) {
      showDeletionFeedback(FEEDBACK.deleted(FEEDBACK_PREFIXES.user, user?.username));
      setUpdateUsers('update');
      resetSelectedUserId();
      closeModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedDeletion(FEEDBACK_PREFIXES.user, user?.username));
    }
  };

  const onUpdatePassword = async () => {
    if (!apiParams || !user) return;
    startLoading();
    const { id, error } = await updateUser(apiParams.clientId, apiParams.subClientId, {
      ...user,
      password: editingUser.password,
    });
    if (!error && id) {
      onCancelChangePassword();
      showPositiveFeedback(`Se actualizó la contraseña de "${user.username}"`);
    } else {
      showFailFeedback(
        error || `Ocurrió un error al actualizar la contraseña de "${user.username}", inténtalo más tarde.`
      );
    }
    stopLoading();
  };

  const onSaveUser = async () => {
    if (!apiParams) return;
    startLoading();
    const { id, error } = isNewUser
      ? await postUser(apiParams.clientId, apiParams.subClientId, editingUser)
      : await updateUser(apiParams.clientId, apiParams.subClientId, editingUser);
    if (!error && id) {
      setUpdateUsers(id);
      if (isNewUser) setSelectedUserId(id);
      showPositiveFeedback(
        isNewUser
          ? FEEDBACK.created(FEEDBACK_PREFIXES.user)
          : FEEDBACK.edited(FEEDBACK_PREFIXES.user, editingUser.username)
      );
    } else {
      showFailFeedback(
        error || isNewUser
          ? FEEDBACK.failedCreation(FEEDBACK_PREFIXES.user)
          : FEEDBACK.failedEdition(FEEDBACK_PREFIXES.user, user?.username)
      );
    }
    stopLoading();
  };

  const onCancelChangePassword = () => {
    setEditingUser((prev) => ({
      ...prev!,
      password: user?.password,
      confirmPassword: user?.confirmPassword,
    }));
    resetChangePassword();
  };

  return (
    <FlexColumn
      classNames={css({
        flex: 1,
        overflow: 'hidden',
        [theme.mediaQuery.large]: {
          maxWidth: '60%',
        },
        [theme.mediaQuery.extraLarge]: {
          maxWidth: '50%',
        },
      })}
    >
      <FlexRow classNames={css({ justifyContent: 'space-between' })}>
        <HeadingSmall margin={0}>{isNewUser ? 'Nuevo usuario' : user?.username}</HeadingSmall>
        {!isNewUser && !changePassword && (
          <StyledButton
            onClick={() => setChangePassword(true)}
            endEnhancer={
              <MaterialIcon
                name="lock"
                size={theme.sizing.scale600}
                color={theme.colors.contentInversePrimary}
              />
            }
          >
            Cambiar Contraseña
          </StyledButton>
        )}
      </FlexRow>
      <FlexColumn classNames={css({ flex: 1, overflow: 'auto' })}>
        <UserForm
          setIsValidEmail={(_) => {}}
          resetEmailField={resetEmailField}
        />
      </FlexColumn>
      <FlexRow
        classNames={`${css({ alignItems: 'center', justifyContent: isNewUser ? 'flex-end' : 'space-between' })}`}
      >
        {!isNewUser && !changePassword ? (
          <StyledButton
            kind="tertiary"
            onClick={openModal}
          >
            Eliminar
          </StyledButton>
        ) : (
          <span />
        )}
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            kind="tertiary"
            onClick={changePassword ? onCancelChangePassword : resetSelectedUserId}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={changePassword ? onUpdatePassword : onSaveUser}
            isLoading={loading}
            disabled={!canSubmit || !hasUpdates}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onDeleteUser}
        confirmText="Eliminar"
        description={<ParagraphMedium>¿Desea eliminar el usuario: "{user?.username}"?</ParagraphMedium>}
      />
    </FlexColumn>
  );
}
